import dayjs from 'dayjs'
import type { AccountInfo } from '@/types'

export const useAuth = () => {
	const profileState = useState<AccountInfo | null>('account')
	const accountName = 'mcg_business_profile'
	const cookieName = 'session'
	const { setAccountInfo } = useUser()
	const { isLoggedIn, User } = reactor(useUser())
	const { saveDraft, loadDraft, clearDraft } = useDraft<{ [accountName]: string }, string>('account_draft')
	const cookie = useCookie(cookieName, {
		sameSite: 'strict',
		path: '/',
		secure: true,
		expires: dayjs().add(72, 'hours').toDate()
	})

	const accountInfo = computed(() => {
		if (User.value) {
			return User.value
		}
		return profileState.value
	})

	const setUser = async (user: AccountInfo | null, encryptUser = true) => {
		if (!user || Object.keys(user).length === 0) {
			return null
		}
		if (encryptUser) {
			// Ignore permission list cause we are not using it here
			const { permissions, ...rest } = user
			// Store to localStorage
			saveDraft({
				[accountName]: stringify(rest)
			})
		}
		profileState.value = user
		setAccountInfo(user)
	}
	const userLocalStore = async () => {
		return resolve(async () => {
			if (loadDraft.value?.[accountName] && !isLoggedIn.value) {
				const response = decryptString<AccountInfo>(loadDraft.value[accountName])
				profileState.value = response
				if (response) {
					setUser(response, false)
				}
				return response
			}
			return null
		})
	}
	const storeToken = (token: string): void => {
		cookie.value = token
	}
	const clearStore = () => {
		clearDraft(true)
		clearNuxtData()
		clearNuxtState()
		removeStorageItem('permissions')
		profileState.value = null
	}
	const clearAllCookies = () => {
		const cookies = document.cookie.split('; ')
		for (const cookie of cookies) {
			const eqPos = cookie.indexOf('=')
			const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie
			document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'
			document.cookie =
				name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.' + window.location.hostname
			document.cookie =
				name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=' + window.location.hostname
		}
	}

	const removeToken = () => {
		cookie.value = null
	}

	const logout = async () => {
		removeToken()
		clearAllCookies()
		clearStore()
		setAccountInfo(null)
		return location.replace('/login')
	}

	// This will clear to make sure it get up to date account information without having to log out
	if (loadDraft.value?.lastSavedAt && isOverAnHour(loadDraft.value.lastSavedAt)) {
		clearDraft()
	}
	userLocalStore()

	return {
		clearAccountStore: clearDraft,
		storage: loadDraft,
		cookie,
		logout,
		accountInfo,
		storeToken,
		setUser,
		clearStore,
		removeToken
	}
}
